import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { SectionFooter, settingsButtonSx, settingsText } from "../settings-section";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { lostSalesService } from "../../utils/services/lost-sales.service";
import {
  LOST_SALES_PARAMETER_HELP,
  LOST_SALES_SETTINGS_KEY,
  draftErrors,
  draftFromParameters,
  draftToValue,
  sameParameters,
} from "../../utils/lostSalesSettings";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const daysFieldSx = {
  width: 150,
  "& .MuiInputBase-root": { fontFamily: "poppins", fontSize: 13.5 },
  "& .MuiFormHelperText-root": { fontFamily: "poppins", mx: 0 },
};

/** One labelled setting: title, explanation, control on the right. */
const SettingRow = ({ title, help, standard, control, children }) => (
  <Box
    sx={{
      py: 2,
      borderBottom: "1px solid var(--border-color)",
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...settingsText.value, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ ...settingsText.muted, mt: 0.25 }}>{help}</Typography>
        {standard && (
          <Typography sx={{ ...settingsText.muted, fontSize: 11.5, mt: 0.5 }}>
            Standard: {standard}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, pt: 0.25 }}>{control}</Box>
    </Box>
    {children}
  </Box>
);

/**
 * Lost Sales Analysis calculation settings for one client
 * (`dashboard.lost_sales` in clientConfig). Defaults, limits and the current
 * values come from the server; it also validates every save and only lets a
 * session edit its own client (internal admins: any client).
 */
const LostSalesSettings = ({ clientId, onSaved, onCancel }) => {
  const [row, setRow] = useState(null);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [rows, availability] = await Promise.all([
        clientConfigService.fetch(clientId, 1, 0, LOST_SALES_SETTINGS_KEY),
        lostSalesService.availability(clientId),
      ]);
      if (!availability?.settings) {
        throw new Error("Lost Sales settings are not available for this client.");
      }
      setRow((rows?.data || []).find((r) => r.key === LOST_SALES_SETTINGS_KEY) || null);
      setSettings(availability.settings);
      setDraft(draftFromParameters(availability.settings.parameters, availability.settings.limits));
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load the Lost Sales settings."));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={14} />
        <Typography sx={settingsText.muted}>Loading…</Typography>
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <Typography sx={settingsText.muted}>{loadError}</Typography>
        <Button size="small" onClick={load} sx={settingsButtonSx}>
          Retry
        </Button>
      </Box>
    );
  }

  const { defaults, limits } = settings;
  const errors = draftErrors(draft, limits);
  const hasErrors = Object.keys(errors).length > 0;
  const value = hasErrors ? null : draftToValue(draft);
  const unchanged = value && sameParameters(value.parameters, settings.parameters);
  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      if (row?._id) {
        await clientConfigService.edit(row._id, {
          value,
          expected_updated_at: row.updatedAt,
        });
      } else {
        await clientConfigService.create({
          client_id: clientId,
          key: LOST_SALES_SETTINGS_KEY,
          value,
        });
      }
      toast.success("Lost Sales settings saved.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't save the Lost Sales settings."));
    } finally {
      setSaving(false);
    }
  };

  const restoreStandard = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Restore the standard Lost Sales calculation settings?")) return;
    setSaving(true);
    try {
      await clientConfigService.delete(row._id);
      toast.success("Standard Lost Sales settings restored.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't restore the standard settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="lost-sales-settings">
      <Typography sx={settingsText.muted} data-testid="lost-sales-settings-status">
        {row
          ? `Customized · last saved ${new Date(row.updatedAt).toLocaleString()}${
              row.value?.updated_by?.email ? ` by ${row.value.updated_by.email}` : ""
            }`
          : "Using the standard calculation settings."}
      </Typography>

      {settings.warnings?.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5, fontFamily: "poppins", fontSize: 12.5 }}>
          Some saved values could not be used and the standard value applies instead:{" "}
          {settings.warnings.map((w) => w.id || w.code).join(", ")}.
        </Alert>
      )}

      <Box sx={{ mt: 1 }}>
        <SettingRow
          title="Search window"
          help={LOST_SALES_PARAMETER_HELP.search_window_days}
          standard={`${defaults.search_window_days} days`}
          control={
            <TextField
              size="small"
              value={draft.searchWindowDays}
              onChange={(event) => update({ searchWindowDays: event.target.value })}
              error={Boolean(errors.searchWindowDays)}
              helperText={
                errors.searchWindowDays ||
                `${limits.search_window_days.min}–${limits.search_window_days.max}`
              }
              inputProps={{ inputMode: "numeric", "aria-label": "Search window in days" }}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              sx={daysFieldSx}
            />
          }
        />

        <SettingRow
          title="Day-of-week adjustment"
          help={LOST_SALES_PARAMETER_HELP.dow_adjustment_enabled}
          standard={defaults.dow_adjustment_enabled ? "On" : "Off"}
          control={
            <Switch
              checked={draft.dowAdjustmentEnabled}
              onChange={(event) => update({ dowAdjustmentEnabled: event.target.checked })}
              inputProps={{ "aria-label": "Day-of-week adjustment" }}
            />
          }
        />

        <SettingRow
          title="Nearest safe period"
          help={LOST_SALES_PARAMETER_HELP.demand_window_days}
          standard={
            defaults.demand_window_days ? `${defaults.demand_window_days} days` : "Off"
          }
          control={
            <Switch
              checked={draft.safePeriodEnabled}
              onChange={(event) => update({ safePeriodEnabled: event.target.checked })}
              inputProps={{ "aria-label": "Nearest safe period" }}
            />
          }
        >
          {draft.safePeriodEnabled && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mt: 1.5 }}>
              <TextField
                size="small"
                value={draft.safePeriodDays}
                onChange={(event) => update({ safePeriodDays: event.target.value })}
                error={Boolean(errors.safePeriodDays)}
                helperText={
                  errors.safePeriodDays ||
                  `${limits.demand_window_days.min}–${limits.demand_window_days.max}`
                }
                inputProps={{ inputMode: "numeric", "aria-label": "Safe period in days" }}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                sx={daysFieldSx}
              />
              <Typography sx={{ ...settingsText.muted, pt: 1 }}>
                If no stockout-free run that long exists, the nearest run with
                valid data is used; days with neither are left out.
              </Typography>
            </Box>
          )}
        </SettingRow>
      </Box>

      <Typography sx={{ ...settingsText.muted, fontSize: 11.5, mt: 1 }}>
        Applies to new Lost Sales Analysis runs for this client and pre-fills the
        inventory Lost Sales dialog. Results already on screen are not recalculated.
      </Typography>

      <SectionFooter
        left={
          row && (
            <Button
              onClick={restoreStandard}
              disabled={saving}
              sx={{ ...settingsButtonSx, color: "var(--text-secondary)", px: 1 }}
            >
              Restore standard settings
            </Button>
          )
        }
      >
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{ ...settingsButtonSx, color: "var(--text-secondary)" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={hasErrors || saving || unchanged}
          onClick={save}
          sx={settingsButtonSx}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </SectionFooter>
    </Box>
  );
};

export default LostSalesSettings;
